import { vw } from 'components/common/Common';
import ImgDescCard from 'components/ImgDescCard';
import FAQAccordion from 'pages/charterQuote/components/contents/FAQAccordion';
import Request from 'pages/charterQuote/components/contents/Request';
import CustomersFeedback from 'pages/home/components/CustomersFeedback';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import FleetType from './contents/FleetType';
import PrivateJetQuote from './contents/PrivateJetQuote';
import SafetyExpertise from './contents/SafetyExpertise';

export const IconDesc = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  flex-direction: column;
`;

export const IconDescItem = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  img {
    width: 32px;
  }
`;

export const IconDescItemDesc = styled.span`
  line-height: 1.3;
  font-weight: 400;
  font-size: 16px;
  color: #050a30;
`;

export const SpecialBold = styled.span`
  font-weight: 600;
`;

export const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: #dde2e4;
`;

const howItWorksItems = [
  {
    src: '/imgs/svg/charterQuote/stars.svg',
    alt: 'sparkle icon',
    desc: (
      <IconDescItemDesc>
        Start by filling out the form with your trip details to receive estimated private jet
        charter costs for your charter flight. If you have any questions or want to discuss further
        details - from landing fees to passenger capacity - our charter sales reps are ready to
        help.
      </IconDescItemDesc>
    ),
  },
  {
    src: '/imgs/svg/charterQuote/confirm-book.svg',
    alt: 'contract icon',
    desc: (
      <IconDescItemDesc>
        After reviewing your personalized private charter quote through our easy-to-use system,
        we'll confirm your booking and finalize all the details, including your flight schedule,
        crew members, and any special requests.
      </IconDescItemDesc>
    ),
  },
  {
    src: '/imgs/svg/charterQuote/plane-car.svg',
    alt: 'transport icon',
    desc: (
      <IconDescItemDesc>
        From there, our team will handle the logistics to provide a smooth flight experience
        tailored to your preferences.{' '}
        <SpecialBold>
          All that's left is to show up and enjoy a luxurious, stress-free journey
        </SpecialBold>
        .
      </IconDescItemDesc>
    ),
  },
];

const pricingDetailItems = [
  {
    src: '/imgs/svg/charterQuote/plane.svg',
    alt: 'plane icon',
    desc: (
      <IconDescItemDesc>
        Your quote will typically include flight time, calculated based on the aircraft's hourly
        rate and the estimated duration of your flight. Fuel surcharges are also included, as they
        fluctuate depending on factors such as aircraft, distance, and current pricing.
      </IconDescItemDesc>
    ),
  },
  {
    src: '/imgs/svg/charterQuote/fee.svg',
    alt: 'document icon',
    desc: (
      <IconDescItemDesc>
        Additionally, crew fees cover the cost of the pilots and other crew members, including per
        diem, meals, and lodging if necessary. Additional fees, including hangar fees and overnight
        fees, can vary by airport, depending on its size and any additional services you’ve
        requested
      </IconDescItemDesc>
    ),
  },
  {
    src: '/imgs/svg/charterQuote/ticket.svg',
    alt: 'discount icon',
    desc: (
      <IconDescItemDesc>
        Finally, for domestic flights, a Federal Excise Tax (FET) of 7.5% is applied to the total
        charges. It's important to note that while the estimated quote provides a range, these
        components may fluctuate depending on availability and other factors.
      </IconDescItemDesc>
    ),
  },
];

const MainContainer = styled.div`
  --home-section-padding: ${vw(240)};
  --home-section-width: calc(100vw - var(--home-section-padding));
  padding: 100px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 200px;
`;

const IconTypeDesc = ({ items = [] }) => {
  return (
    <IconDesc>
      {items.map((item, index) => (
        <Fragment key={index}>
          <IconDescItem>
            <img src={item.src} alt={item.alt || ''} />
            {item.desc}
          </IconDescItem>
          {index < items.length - 1 && <Separator />}
        </Fragment>
      ))}
    </IconDesc>
  );
};

const LargeAircraft = styled.img`
  width: 100vw;
  object-fit: cover;
`;

const QuestionLists = [
  {
    question: 'How much does a private jet trip cost?',
    answer:
      'Private jet rental costs can vary based on factors like the aircraft you choose, flight duration, market demand, and specific services requested, such as concierge services or overnight parking. For an accurate estimate, we encourage you to request a charter plane quote or contact one of our consultants for assistance.',
  },
  {
    question: 'Is a private jet worth it?',
    answer:
      'For many travelers, the convenience, luxury, and time savings a private jet offers make it well worth the investment. To see if it fits your needs, request a personalized quote using our jet charter cost estimator to weigh your options.',
  },
  {
    question: 'Can I cancel a private jet I booked?',
    answer:
      'Cancellation policies can vary, so we encourage you to contact our team for details specific to your booking.',
  },
];

export default function Description() {
  return (
    <MainContainer>
      <ImgDescCard
        isLeft
        imgSrc="/imgs/webp/charterQuote/how-it-works.webp"
        imgAlt="Modern private jet with boarding stairs deployed"
        title="How it works"
        desc={[
          'Booking your private jet charter service with Airacer is simple and straightforward.',
        ]}
      >
        <IconTypeDesc items={howItWorksItems} />
      </ImgDescCard>

      <Request />

      <ImgDescCard
        isLeft={false}
        imgSrc="/imgs/webp/charterQuote/aircraft-options.webp"
        imgAlt="private jet charter flying over the trees"
        title="Aircraft Options"
        desc={[
          'Choosing the right aircraft is essential for a comfortable and efficient flight. There are various aircraft options that can accommodate different trip requirements, such as longer distances, larger group sizes, and preferred amenities.',
          "Whether you're embarking on a short getaway or a long-haul journey, we can find an aircraft that’s perfect for your needs.",
        ]}
      >
        <img
          src="/imgs/webp/charterQuote/aircraft-img.webp"
          alt="closeup of private jet interior with leather seats and wood details"
        />
      </ImgDescCard>

      <FleetType />

      <LargeAircraft
        src="/imgs/webp/charterQuote/flying-against-clouds.webp"
        alt="private jet charter flying against clouds"
      />

      <ImgDescCard
        isLeft={false}
        imgSrc="/imgs/webp/charterQuote/pricing-details.webp"
        imgAlt="satisfied client preparing to board private jet booked through airacer"
        title="Pricing Details"
        desc={[
          "When requesting a private jet quote, it's important to understand the components that make up the total cost.",
        ]}
      >
        <IconTypeDesc items={pricingDetailItems} />
      </ImgDescCard>

      <PrivateJetQuote />

      <ImgDescCard
        isLeft
        imgSrc="/imgs/webp/charterQuote/why-choose.webp"
        imgAlt="Champagne and gourmet catering aboard luxury jet"
        title="Why Choose Airacer"
        desc={[
          "Booking with Airacer offers the best of convenience, personalized service, and competitive pricing. Whether you're flying for business or leisure, our team will tailor every detail to your needs for a seamless private flight or a luxury trip.",
          'We proudly provide a 360-degree, white-glove service that takes the stress out of planning your next trip.',
        ]}
      >
        <img
          src="/imgs/webp/charterQuote/why-choose-img.webp"
          alt="Luxurious leather seats in private jet cabin"
        />
      </ImgDescCard>

      <SafetyExpertise />

      <CustomersFeedback />

      <ImgDescCard
        isLeft={false}
        imgSrc="/imgs/webp/charterQuote/questions.webp"
        imgAlt="private jet preparing for takeoff on the tarmac"
        title="Frequently Asked Questions"
        desc={[
          'Do you have more questions about getting a private jet quote? Airacer has answers.',
        ]}
      >
        <FAQAccordion QuestionLists={QuestionLists} />
      </ImgDescCard>
    </MainContainer>
  );
}
